import { useState, useRef } from "react";
import { X, Loader2, Eye, EyeOff } from "lucide-react";
import { addUser } from "../api/auth";

export default function AddUserDrawer({ isOpen, onClose }) {
  const registrationRef = useRef(null);

  const [form, setForm] = useState({
    department: "sales",
    employeeId: "",
    email: "",
    contact: "",
    password: "",
    username: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const validateForm = () => {
    let newErrors = {};

    if (!form.employeeId)
      newErrors.employeeId = "This is a required field";

    if (!form.email)
      newErrors.email = "This is a required field";

    if (!form.contact)
      newErrors.contact = "This is a required field";

    if (!form.password)
      newErrors.password = "This is a required field";

    if (!form.username)
      newErrors.username = "This is a required field";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setForm({
      department: "sales",
      employeeId: "",
      email: "",
      contact: "",
      password: "",
      username: "",
    });
    setErrors({});
    setApiError("");
    setShowPassword(false);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setApiError("");
    setSubmitting(true);

    try {
      const response = await addUser({
        department: form.department,
        employee_id: form.employeeId,
        username: form.username,
        email: form.email,
        contact: form.contact,
        password: form.password,
      });

      if (response.data && response.data.success) {
        resetForm();
        onClose();
      } else {
        setApiError(response.data?.message || "Failed to add user.");
      }
    } catch (error) {
      setApiError(
        error.response?.data?.message || "Failed to add user. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}

      <div
        className="hidden sm:block flex-1 bg-black/20"
        onClick={handleClose}
      ></div>

      {/* Drawer */}

      <div className="w-full sm:w-[85%] md:w-[65%] max-w-full sm:max-w-2xl bg-white h-full shadow-xl flex flex-col ml-auto animate-in slide-in-from-right">

        {/* Header */}

        <div className="flex justify-between items-center px-4 sm:px-6 py-3 border-b">

          <h2 className="text-lg font-semibold text-gray-800">
            Add User
          </h2>

          <button onClick={handleClose}>
            <X />
          </button>

        </div>

        {/* Body */}

        <div className="flex flex-col sm:flex-row flex-1 overflow-hidden">

          {/* Left Menu */}

          <div className="w-full sm:w-56 shrink-0 border-b sm:border-b-0 sm:border-r p-3 sm:p-5 flex sm:block overflow-x-auto sm:overflow-visible">

            <div
              onClick={() => scrollToSection(registrationRef)}
              className="cursor-pointer whitespace-nowrap text-gray-600 hover:text-blue-600 font-medium"
            >
              Registration Information
            </div>

          </div>

          {/* Form */}

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">

            {apiError && (
              <div className="mb-5 bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-2">
                {apiError}
              </div>
            )}

            <section ref={registrationRef}>

              <h3 className="font-semibold text-gray-800 mb-6">
                User Registration
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                {/* Department */}

                <div>
                  <label className="text-xs text-gray-500">
                    Department
                    <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={form.department}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        department: e.target.value,
                      })
                    }
                    className="w-full mt-1 border rounded px-3 py-2 text-sm bg-white"
                  >
                    <option value="sales">Sales</option>
                    <option value="preSales">Pre-Sales</option>
                    <option value="BidManager">Bid Manager</option>
                  </select>
                </div>

                {/* Employee ID */}

                <div>
                  <label className="text-xs text-gray-500">
                    Employee ID
                    <span className="text-red-500">*</span>
                  </label>

                  <input
                    value={form.employeeId}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        employeeId: e.target.value,
                      })
                    }
                    className={`w-full mt-1 border rounded px-3 py-2 text-sm ${
                      errors.employeeId
                        ? "border-red-500"
                        : ""
                    }`}
                  />

                  {errors.employeeId && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.employeeId}
                    </p>
                  )}
                </div>

                {/* Email */}

                <div>
                  <label className="text-xs text-gray-500">
                    Email
                    <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        email: e.target.value,
                      })
                    }
                    className={`w-full mt-1 border rounded px-3 py-2 text-sm ${
                      errors.email
                        ? "border-red-500"
                        : ""
                    }`}
                  />

                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Contact */}

                <div>
                  <label className="text-xs text-gray-500">
                    Contact Number
                    <span className="text-red-500">*</span>
                  </label>

                  <input
                    value={form.contact}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        contact: e.target.value,
                      })
                    }
                    className={`w-full mt-1 border rounded px-3 py-2 text-sm ${
                      errors.contact
                        ? "border-red-500"
                        : ""
                    }`}
                  />

                  {errors.contact && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.contact}
                    </p>
                  )}
                </div>

                {/* Password */}

                <div>
                  <label className="text-xs text-gray-500">
                    Password
                    <span className="text-red-500">*</span>
                  </label>

                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          password: e.target.value,
                        })
                      }
                      className={`w-full mt-1 border rounded px-3 py-2 pr-9 text-sm ${
                        errors.password
                          ? "border-red-500"
                          : ""
                      }`}
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 mt-0.5 text-gray-400 hover:text-gray-600"
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>

                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Username */}

                <div>
                  <label className="text-xs text-gray-500">
                    Username
                    <span className="text-red-500">*</span>
                  </label>

                  <input
                    value={form.username}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        username: e.target.value,
                      })
                    }
                    className={`w-full mt-1 border rounded px-3 py-2 text-sm ${
                      errors.username
                        ? "border-red-500"
                        : ""
                    }`}
                  />

                  {errors.username && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.username}
                    </p>
                  )}
                </div>

              </div>

            </section>

          </div>

        </div>

        {/* Footer */}

        <div className="border-t px-4 sm:px-6 py-4 flex flex-wrap justify-end gap-3">

          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-5 py-2 border rounded disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={submitting}
            className="px-5 py-2 bg-blue-600 text-white rounded flex items-center gap-2 disabled:opacity-60"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? "Saving..." : "Register / Save"}
          </button>

        </div>

      </div>

    </div>
  );
}